import { HoldsInfo } from '@store/holdsTab/types';
import type { EscalationCaseDto } from '@store/creditApplications/types';
import { OldLoad } from '@store/loadboard/oldLoads/types';
import type { PcMilerError, PostingMode } from '@store/orders/types';

export type { EscalationCaseDto };

export type WSMessageT = {
  dataJson: string;
  eventName: WSEventTypeEnum;
  groupsToSend: string[];
  groupName: string | null;
  hasGroup: boolean;
  leaveGroup: boolean;
  requestId: string;
  correlationId: string;
  sendToAll: boolean;
  id: number | null;
  userId: number | null;
  firstName: string | null;
  lastName: string | null;
};

export enum WSEventTypeEnum {
  Created = 'Load:Created',
  Updated = 'Load:Updated',
  Deleted = 'Load:Deleted',
  View = 'orderview',
  Leave = 'orderleave',
  Save = 'ordersave',
  Lock = 'orderlock',
  Transfer = 'ordertransfer',
  Unlock = 'orderunlock',
  BidChanged = 'Bid:Changed',

  // Global Alerts
  RequestApproval = 'Request:Approval',
  CreditAppStatusChange = 'CreditApp:StatusChange',
  CreditAppClaimChange = 'CreditApp:ClaimChange',
  FlagCleared = 'Flag:Cleared',
  ApprovalComment = 'Approval:Comment',
  RequestCheck = 'Request:Check',
  RequestCheckAddressed = 'Request:Check:Addressed',
  RequestNewCustomer = 'Request:New Customer',
  RequestCredit = 'Request:Credit',
  RequestCreditAddressed = 'Request:Credit:Addressed',
  RequestCreditClaimChange = 'Request:Credit:ClaimChange',
  CustomerPendingClaimChange = 'Customer:Pending:ClaimChange',
  CustomerDoNotSolicit = 'Customer:DoNotSolicit',
  Notification = 'Notification',
  NewAnnouncement = 'New Announcement',
  DeletedAnnouncement = 'Deleted Announcement',

  // Capacity finder
  CapFinderAtgInternalCompleted = 'CapFinderCompleted:Atg:Internal',
  CapFinderAtgSnowflakeCompleted = 'CapFinderCompleted:Atg:Snowflake',
  CapFinderFmcsaCompleted = 'CapFinderCompleted:Fmcsa',
  CapFinderDatCompleted = 'CapFinderCompleted:DAT',
  CapFinderTruckstopCompleted = 'CapFinderCompleted:Truckstop',
  CapFinderGenlogsCompleted = 'CapFinderCompleted:Genlogs',
  CapFinderSummaryCompleted = 'CapFinderCompleted:Summary',
  CapFinderSearchCompleted = 'CapFinderCompleted:All',
  CapFinderLiveOffers = 'CapFinder:LiveOffers',
  CapFinderLiveTrucksDat = 'CapFinder:LiveTrucks:Dat',
  CapFinderLiveTrucksTruckstop = 'CapFinder:LiveTrucks:Truckstop',
  CapFinderLiveTrucksArmstrong = 'CapFinder:LiveTrucks:Armstrong',
  CapFinderLiveTrucksGenlogs = 'CapFinder:LiveTrucks:Genlogs',
  CapFinderLoadHistory = 'CapFinder:LoadHistory',
  CapFinderOfferHistory = 'CapFinder:OfferHistory',
  CapFinderPotentialDropoffs = 'CapFinder:PotentialDropoffs',
  CapFinderPreferredLanes = 'CapFinder:PreferredLanes',
  CapFinderHistoricPostedTrucks = 'CapFinder:HistoricPostedTrucks',
  CapFinderCarrierDomicile = 'CapFinder:CarrierDomicile',
  CapFinderGenlogsHistory = 'CapFinder:GenlogsHistory',

  // Revenue Search export
  RevenueSearchExportStatusChanged = 'RevenueSearchExport:StatusChanged',

  // Billing queues
  BillingQueueTransfloAdded = 'Transflo:BillingQueueAdded',
  BillingQueueTransfloImported = 'Transflo:ItemImportedUnknown',
  BillingQueueTransfloReview = 'Transflo:ItemImportedReview',
  BillingQueueRemove = 'Queue:Remove',
  PendingApprovalsQueueAdd = 'PendingApprovalsQueue:Add',
  PendingApprovalsQueueRemove = 'PendingApprovalsQueue:Remove',
}

export type AnnouncementMessagesType = {
  id: number;
  createdDate: string;
  messageType: string;
  message: string;
  createdBy: string;
  thumbnail: string;
  read: boolean;
  context: string | null;
  objectId: string | null;
  payloadJson?: string;
  payloadVersion?: number;
};

export type CreditIncreaseRequestCustomer = {
  id: number;
  objectId: number;
  requestDate: string;
  requestedBy: string;
  for: number;
  details: string;
  customer: string;
  claimedBy?: string | null;
  claimedDate?: string | null;
  claimedByComment?: string | null;
};

export type ClaimCreditIncreasePayload = {
  id: number;
  claim: boolean;
  comment?: string;
};

export type PendingCustomers = {
  id: number;
  name: string;
  agent: string;
  status: null | string;
  type: string;
  pendingLoad: string;
  createdDate: string;
  canHide: boolean;
  claimedByUserId?: number | null;
  claimedBy?: string | null;
  claimedDate?: string | null;
  claimedByComment?: string | null;
};

export type ClaimPendingCustomerPayload = {
  id: number;
  claim: boolean;
  comment?: string;
};

export type DoNotSolicitRequest = {
  id: number;
  name: string;
  agent: string;
};

export type NotificationTypes = {
  id: number;
  createdDate: string;
  messageType: string;
  message: string;
  createdBy: string;
  thumbnail: string;
  read: boolean;
  context: string;
  objectId: string | null;
  payloadJson?: string;
  payloadVersion?: number;
};

export type NeedPaperworkTypes = {
  loadNumber: string;
  flagId: number;
  flagDate: string;
  orderId: number;
  requestedBy: string;
  customer: string;
  customerId: number;
  carrier: string;
  carrierId: number;
  orderLoadSeq: number;
  carrierContactName: string;
  carrierContactPhone: string;
  carrierContactEmail: string;
  carrierContactType: string;
  carrierDOT: string;
  carrierMC: string;
  orderStatus: string;
  loadStatus: string;
  description: string;
  agent: string;
  flagHistoryComment: string;
  flagHistoryCreatedDate: string;
  flagHistoryUser: string;
  flagHistoryId: number;
};

export type PendingChecksTypes = {
  id: number;
  objectId: string;
  requestDate: string;
  requestedBy: string;
  for: number;
  details: string;
  loadNum: string;
  loadLineItemType: string;
  carrierId: number;
};

export type PreviousCheckTypes = {
  date: string;
  amount: number;
  description: string;
};

export type CheckRequestType = {
  requestId: number;
  agent: string;
  carrier: string;
  customer: string;
  requestDate: string;
  amount: number;
  comment: string;
  carrierPay: number;
  loadNum: string;
  previousChecks: PreviousCheckTypes[];
  carrierFirstLoad: boolean;
  loadsCompleted: number;
};

export type AllApprovalsTypes = {
  id: number;
  ltl: boolean;
  description: string;
  setDate: string;
  setBy: string;
  order: number;
  agent: string;
  agentId: number;
  office: string;
  accountingGroup: number;
  lastCommentDate: string | null;
  lastComment: string | null;
  lastCommentBy: string | null;
  needsAttention: boolean;
  overdue: boolean;
  pastDue: boolean;
  thirdParty: boolean;
  quickpay: boolean;
  customerName: string;
  canadian: boolean;
  isDisputed: boolean;
  carrierName: string;
  currentState: string | null;
  stateChangedAt: string | null;
};

export type PcMilerApiResponse = {
  address: {
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    county: string;
    country: string;
    splc: string | null;
    countryPostalFilter: number;
    abbreviationFormat: number;
    stateName: string;
    stateAbbreviation: string;
    countryAbbreviation: string;
  };
  coords: {
    lat: string;
    lon: string;
  };
  region: number;
  label: string;
  placeName: string;
  timeZone: string;
  errors: PcMilerError[];
  speedLimitInfo: string | null;
  confidenceLevel: string;
  distanceFromRoad: number | null;
  crossStreet: string | null;
  timeZoneOffset: string;
  timeZoneAbbreviation: string;
  isDst: boolean;
}[];

export interface AvailablePosting {
  id: number;
  loadId: number | null;
  boardsPostedTo: string[];
  isQuickPost: boolean;
  origin: string;
  originCity: string;
  originState: string;
  originCountry: string;
  destination: string;
  destinationCity: string;
  destinationState: string;
  destinationCountry: string;
  pickUp: string;
  dropOff: string;
  price: number | null;
  equipment: string;
  comment: string;
  drops: number;
  height: number | null;
  length: number | null;
  width: number | null;
  datePosted: string;
  reference: string | null;
  volume: number | null;
  weight: number | null;
  tags: string[];
  availableServices: string[] | null;
  servicesToPostTo: string[] | null;
  loadNumber: string;
  mode?: PostingMode | null;
  fullTruckload: boolean;
  postedBy: string;
  mileage: number;
  internalNote: string | null;
  contactPhone: string | null;
  originZip: string | null;
  destinationZip: string | null;
  boardsPosted: string;
  tagsList: string;
  errors: unknown;
  loadMaxPrice?: number | null;
  atgOverride?: string | null;
  datOverride?: string | null;
  tsOverride?: string | null;
  tsSubcodeOverride?: string | null;
  mailerOverride?: string | null;
}

export type SidebarDataStoreType = {
  annoucementsData: {
    loading: boolean;
    data: AnnouncementMessagesType[];
  };
  pendingCustomers: {
    loading: boolean;
    data: PendingCustomers[];
  };
  doNotSolicitRequests: {
    loading: boolean;
    data: DoNotSolicitRequest[];
  };
  creditIncreaseRequestCustomers: {
    loading: boolean;
    data: CreditIncreaseRequestCustomer[];
  };
  escalations: {
    loading: boolean;
    data: EscalationCaseDto[];
  };
  pendingChecksData: {
    loading: boolean;
    data: PendingChecksTypes[];
  };
  needPaperworkData: {
    loading: boolean;
    data: NeedPaperworkTypes[];
  };
  alertsData: {
    loading: boolean;
    data: NotificationTypes[];
  };
  approvalsData: {
    loading: boolean;
    loaded: boolean;
    allApprovals: AllApprovalsTypes[];
    disputedLoads: AllApprovalsTypes[];
    approvalGrouping: { [key: string]: AllApprovalsTypes[] };
    disputedGrouping: { [key: string]: AllApprovalsTypes[] };
  };
  overdueApprovals: {
    loading: boolean;
    data: PendingApprovals[];
  };
  approvals: {
    loading: boolean;
    data: PendingApprovals[];
  };
  isCollapsed: boolean;
};

export interface PendingApprovals {
  id: number;
  ltl: boolean | null;
  description: string;
  setDate: string;
  setBy: string;
  order: number;
  agent: string;
  agentId: number;
  office: string;
  accountingGroup: number | null;
  lastCommentDate: string | null;
  lastComment: string | null;
  lastCommentBy: string | null;
  needsAttention: boolean | null;
  overdue: boolean | null;
  pastDue: boolean | null;
  thirdParty: boolean | null;
  quickpay: boolean | null;
  customerName: string;
  canadian: boolean | null;
  isDisputed: boolean | null;
  carrierName: string;
  currentState: string | null;
  stateChangedAt: string | null;
}

export type WarningStoreType = {
  billingHolds: {
    loading: boolean;
    data: HoldsInfo[];
  };
  oldLoad: {
    loading: boolean;
    data: {
      data: OldLoad[];
      totalCount: number;
    };
  };
  warningComponent: {
    height: number;
    visible: boolean;
  };
};
